import classNames from "classnames";
import { Button } from "components/DesignSystem/Button/Button";
import ToolTip from "components/design/toolTip";
import { BookIcon } from "components/icons/BookIcon";
import { RocketIconWithBorder } from "components/icons/RocketIconWithBorder";
import { SmallCircleGreenTick } from "components/icons/SmallCircleGreenTick";
import { SmallWhiteTick } from "components/icons/SmallWhiteTick";
import { motion } from "framer-motion";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useModal } from "hooks/useModal";
import { useAppSelector } from "hooks/useAppSelector";
import DiamondWithBg from "static/images/DiamondWithBg.svg";
import {
  BooksOnboardingStatus,
  OnboardingStatusType,
} from "store/apis/productOnboarding";
import { useGetSubscriptionsQuery } from "store/apis/subscriptions";
import { BOOKS_PLUS_PLAN } from "constants/addons";
import dayjs from "dayjs";
import { BooksSoftwareSubscriptionsModal } from "components/BooksSoftwareSubscriptionsModal";
import { ReferralBanner } from "components/home/TaxComponents/ReferralBanner";

export type OnboardingStep = {
  title: string;
  description: string;
  onClick: () => void;
  isLoading?: boolean;
  status: "NOT_STARTED" | "IN_PROGRESS" | "COMPLETED";
  tooltipText?: string;
};

type LoadingStepConfig = {
  title: string;
  description: string;
  statusKey: keyof BooksOnboardingStatus;
};

const LOADING_STEPS: LoadingStepConfig[] = [
  {
    title: "Pulling transaction data",
    description:
      "Importing your complete transaction history from connected bank and card accounts",
    statusKey: "transaction_pull_status",
  },
  {
    title: "AI categorizing transactions",
    description: "Running AI categorization pipeline across all transactions",
    statusKey: "transaction_categorization_status",
  },
  {
    title: "Loading metrics",
    description: "Calculating key financial metrics across all your accounts",
    statusKey: "loading_metrics_status",
  },
];

const HEADER_LOADING_TITLE = "We are getting your Books ready!";
const HEADER_LOADING_DESCRIPTION = "Sit tight while we work our magic";
const HEADER_DEFAULT_TITLE = "Get started";
const HEADER_DEFAULT_DESCRIPTION =
  "Set up your account and get your first financial insights";

const PulsingShadow = () => (
  <motion.div
    className="t-absolute t-inset-0 t-rounded-full t-z-10"
    animate={{
      boxShadow: [
        "0 0 0 0px rgba(86, 34, 255, 0)",
        "0 0 0 6px rgba(86, 34, 255, 0.15)",
        "0 0 0 0px rgba(86, 34, 255, 0)",
      ],
    }}
    transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
  />
);

export const StepRow = ({
  title,
  description,
  isActive,
  isCompleted,
  isDisabled,
  isLast,
  showPulse,
  action,
}: {
  title: string;
  description: string;
  isActive?: boolean;
  isCompleted?: boolean;
  isDisabled?: boolean;
  isLast: boolean;
  showPulse?: boolean;
  action?: React.ReactNode;
}) => (
  <div className="t-flex t-gap-4 t-items-stretch">
    <div className="t-flex t-flex-col t-items-center">
      <div className="t-relative t-shrink-0">
        {showPulse && <PulsingShadow />}
        <div
          className={classNames(
            "t-h-6 t-w-6 t-rounded-full t-border-[1.5px] t-border-solid t-flex t-items-center t-justify-center",
            {
              "t-border-purple-50 t-bg-purple-10": isActive,
              "t-border-dark_green-50 t-bg-dark_green-50": isCompleted,
              "t-border-neutral-20 t-bg-neutral-0": !isActive && !isCompleted,
            }
          )}
        >
          {isCompleted && <SmallWhiteTick />}
        </div>
      </div>
      {!isLast && <div className="t-w-0.5 t-flex-1 t-bg-purple-10" />}
    </div>
    <div
      className={classNames(
        "t-flex t-justify-between t-items-start t-gap-4 t-flex-1",
        { "t-pb-5": !isLast }
      )}
    >
      <div className="t-flex t-flex-col t-gap-0.5">
        <span
          className={classNames("t-text-subtext", {
            "t-text-text-100": !isDisabled,
            "t-text-neutral-30": isDisabled,
          })}
        >
          {title}
        </span>
        <span
          className={classNames("t-text-body-sm", {
            "t-text-text-30": !isDisabled,
            "t-text-neutral-20": isDisabled,
          })}
        >
          {description}
        </span>
      </div>
      {action}
    </div>
  </div>
);

const LoadingState = ({
  statusData,
}: {
  statusData?: BooksOnboardingStatus;
}) => (
  <div className="t-p-6">
    {LOADING_STEPS.map((step, index) => {
      const status: OnboardingStatusType =
        statusData?.[step.statusKey] ?? "NOT_STARTED";
      const isCompleted = status === "COMPLETED";
      const isActive = status === "IN_PROGRESS";
      const isDisabled = !isCompleted && !isActive;

      return (
        <StepRow
          key={step.title}
          title={step.title}
          description={step.description}
          isActive={isActive}
          isCompleted={isCompleted}
          isDisabled={isDisabled}
          isLast={index === LOADING_STEPS.length - 1}
          showPulse={isActive}
        />
      );
    })}
  </div>
);

const DefaultState = ({ steps }: { steps: OnboardingStep[] }) => {
  const activeStepIndex = steps.findIndex(
    (step) => step.status !== "COMPLETED"
  );

  return (
    <div className="t-p-6">
      {steps.map((step, index) => {
        const isActive = index === activeStepIndex;
        const isCompleted = step.status === "COMPLETED";
        const isDisabled = !isActive && !isCompleted;

        return (
          <StepRow
            key={step.title}
            title={step.title}
            description={step.description}
            isActive={isActive}
            isCompleted={isCompleted}
            isDisabled={isDisabled}
            isLast={index === steps.length - 1}
            showPulse={isActive}
            action={
              isActive ? (
                step.tooltipText ? (
                  <ToolTip text={step.tooltipText}>
                    <div>
                      <Button size="small" customType="primary" disabled>
                        Start now
                      </Button>
                    </div>
                  </ToolTip>
                ) : (
                  <Button
                    size="small"
                    customType="primary"
                    onClick={step.onClick}
                    isLoading={step.isLoading}
                    disabled={step.isLoading}
                  >
                    Start now
                  </Button>
                )
              ) : undefined
            }
          />
        );
      })}
    </div>
  );
};

const UpgradeBanner = ({
  onUpgradeClick,
  trialEndDate,
}: {
  onUpgradeClick: () => void;
  trialEndDate?: string;
}) => (
  <div className="t-border t-border-solid t-border-neutral-10 t-rounded-lg t-shadow-light-30 t-flex">
    <div className="t-bg-gradient-to-br t-from-purple-50 t-via-white t-to-yellow-50 t-flex t-items-center t-justify-center">
      <img
        src={DiamondWithBg}
        alt="Diamond"
        className="t-w-44 t-h-44 t-object-contain"
      />
    </div>

    <div className="t-flex t-flex-col t-justify-center t-gap-2 t-px-10 t-py-4">
      <h3 className="t-text-subtext t-text-text-100">
        Experience Inkle Books Standard plan for free
      </h3>
      <div className="t-flex t-flex-col t-gap-2">
        <div className="t-flex t-gap-2 t-items-center">
          <span className="t-flex t-items-center t-gap-2 t-text-dark_green">
            <SmallCircleGreenTick />
          </span>
          <span className="t-text-body-sm t-text-text-30">
            Unlock richer reports
          </span>
        </div>

        <div className="t-flex t-items-center t-gap-2">
          <span className="t-flex t-items-center t-gap-2 t-text-dark_green">
            <SmallCircleGreenTick />
          </span>
          <span className="t-text-body-sm t-text-text-30">
            Smarter automation and powerful insights into your books.
          </span>
        </div>
      </div>
      <div>
        <span className="t-text-body-sm t-italic t-text-text-30">
          {trialEndDate
            ? `Start today → Trial ends ${dayjs(trialEndDate).format(
                "MMM DD, YYYY"
              )}. → Plan will auto-renew until cancelled after the trial. `
            : "Start your free trial today."}
        </span>{" "}
        <Button customType="link" size="xsmall" onClick={onUpgradeClick}>
          Start free trial
        </Button>
      </div>
    </div>
  </div>
);

export const GetStartedCard = ({
  steps,
  isLoading,
  statusData,
}: {
  steps: OnboardingStep[];
  isLoading?: boolean;
  statusData?: BooksOnboardingStatus;
}) => {
  const { uuid: groupId } = useCurrentGroupContext();
  const entityIdOnSelect = useAppSelector(
    (state) => state.onboardingWidget.entityId
  );
  const entityId = useCurrentEntityId() || entityIdOnSelect!;
  const upgradeModal = useModal();

  const { data: booksPlans = [] } = useGetSubscriptionsQuery(
    { subscription_types: BOOKS_PLUS_PLAN, groupId, entityId },
    { skip: !groupId || !entityId }
  );

  const isOnPaidPlan = booksPlans.some(
    (plan) => plan.group_subscription?.length
  );
  const trialEndDate = booksPlans.find(
    (plan) => plan.trail_period_end_date
  )?.trail_period_end_date;

  const headerTitle = isLoading ? HEADER_LOADING_TITLE : HEADER_DEFAULT_TITLE;
  const headerDescription = isLoading
    ? HEADER_LOADING_DESCRIPTION
    : HEADER_DEFAULT_DESCRIPTION;

  return (
    <>
      <div className="t-border t-border-solid t-border-neutral-10 t-rounded-lg t-shadow-light-30 t-overflow-hidden">
        <div className="t-bg-[linear-gradient(90deg,rgba(236,229,255,0.9)_0%,rgba(236,229,255,0.1)_100%)] t-p-6 t-flex t-items-center t-gap-3 rounded-xls t-m-1 t-rounded-lg">
          <div className="t-h-10 t-w-10 t-rounded-full t-bg-purple-10 t-flex t-items-center t-justify-center t-shadow-[0_0_0_5px_rgba(255,255,255,0.5)]">
            {isLoading ? <RocketIconWithBorder /> : <BookIcon />}
          </div>
          <div className="t-flex t-flex-col t-gap-0.5">
            <span className="t-text-subtitle-lg t-font-semibold t-font-we">
              {headerTitle}
            </span>
            <span className="t-text-body-sm t-text-text-30">
              {headerDescription}
            </span>
          </div>
        </div>
        {isLoading ? (
          <LoadingState statusData={statusData} />
        ) : (
          <DefaultState steps={steps} />
        )}
      </div>
      {isLoading && (
        <div className="t-flex t-flex-col t-gap-6 t-mt-6">
          {!isOnPaidPlan && (
            <div>
              <UpgradeBanner
                onUpgradeClick={upgradeModal.open}
                trialEndDate={trialEndDate}
              />
            </div>
          )}
          <div>
            <ReferralBanner />
          </div>
        </div>
      )}
      <BooksSoftwareSubscriptionsModal
        open={upgradeModal.isOpen}
        onClose={upgradeModal.close}
        entityId={entityId}
        onProductSubscribe={upgradeModal.close}
      />
    </>
  );
};
